import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import {
  AddPaymentMethodCreditCardEntry,
  AddPaymentMethodWrapper,
} from "Payments/AddPaymentMethod";

import FnrModal from "UI/FnrModal";

import AddPaymentMethodBankAccountEntry from "../../AddPaymentMethod/AddPaymentMethodBankAccountEntry";

import AdditionalSettings from "./AdditionalSettings";
import CloseButton from "./CloseButton";

export default function AddPaymentMethodModal({
  show,
  setShow,
  setPaymentMethods,
  mustSaveCard,
  userID,
  hasMobilePhone,
  sharableWith = [],
  existingPaymentMethod = null,
  hasOtherPaymentMethods,
  isAlreadyExisting,
  existingUserName,
  existingZip,
  tabID,
  networkName,
  initialPaymentType,
  onSuccess,
  initialReplaceExistingCard = false,
  showUpdateUI,
  initialACHAccountType,
  initialAccountHolderType,
  initialShouldRetokenize,
}) {
  const handleSuccess = ({ paymentMethods }) => {
    if (onSuccess) onSuccess();
    setPaymentMethods(paymentMethods);
    setShow(false);
  };

  const handleDeleteSuccess = ({ paymentMethods }) => {
    setWasDeleted(true);
    setTemporaryPaymentMethods(paymentMethods);
    setShow(false);
  };

  const [temporaryPaymenMethods, setTemporaryPaymentMethods] = useState(false);
  const [wasDeleted, setWasDeleted] = useState(false);

  const finishedClosing = () => {
    if (wasDeleted && temporaryPaymenMethods !== false)
      setPaymentMethods(temporaryPaymenMethods);
  };

  const [inputErrors, setInputErrors] = useState([]);
  const [shouldClearDisplayMessages, setShouldClearDisplayMessages] =
    useState(false);

  const [maxHeight, setMaxHeight] = useState(0);

  const updateMaxHeight = () => {
    setMaxHeight(window.innerHeight - 80);
  };

  useEffect(() => {
    if (shouldClearDisplayMessages) {
      setInputErrors([]);
    }
  }, [shouldClearDisplayMessages]);

  useEffect(() => {
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, []);

  useEffect(() => {
    const fnReactHolder = document.getElementById("fnReactHolder");
    if (!fnReactHolder) return;
    if (show) {
      // prevents tabbing to elements behind the modal
      fnReactHolder.setAttribute("inert", true);
    } else {
      fnReactHolder.removeAttribute("inert");
    }
  }, [show]);

  const positionTopIfMobile = window.innerHeight < 860;

  const paymentMethodID = existingPaymentMethod?.ID
    ? {
        paymentMethodID: existingPaymentMethod.ID,
      }
    : null;

  return (
    <FnrModal
      show={show}
      setShow={setShow}
      positionTopIfMobile={positionTopIfMobile}
      onFinishedClosing={finishedClosing}
    >
      <AddPaymentMethodModalContent style={{ maxHeight }}>
        <AddPaymentMethodWrapper
          onSuccess={handleSuccess}
          onDeleteSuccess={handleDeleteSuccess}
          mustSaveCard={mustSaveCard}
          userID={userID}
          existingPaymentMethod={{
            ...existingPaymentMethod,
            ...paymentMethodID,
          }}
          hasMobilePhone={hasMobilePhone}
          sharableWith={sharableWith}
          hasOtherPaymentMethods={hasOtherPaymentMethods}
          setInputErrors={setInputErrors}
          tabID={tabID}
          existingUserName={existingUserName}
          initialReplaceExistingCard={
            initialReplaceExistingCard || existingPaymentMethod?.needsUpdate
          }
          showUpdateUI={showUpdateUI}
          initialACHAccountType={initialACHAccountType}
          initialAccountHolderType={initialAccountHolderType}
          initialShouldRetokenize={
            initialShouldRetokenize || existingPaymentMethod?.needsUpdate
          }
        >
          <CreditCardWrapper>
            {!existingPaymentMethod && (
              <CloseButton
                setShouldClearDisplayMessages={setShouldClearDisplayMessages}
                setInputErrors={setInputErrors}
              />
            )}
            <AddPaymentMethodCreditCardEntry
              show
              setInputErrors={setInputErrors}
              shouldClearDisplayMessages={shouldClearDisplayMessages}
              setShouldClearDisplayMessages={setShouldClearDisplayMessages}
              existingUserName={existingUserName}
              existingZip={existingZip}
              initialPaymentType={initialPaymentType}
              initialAppState={{
                isEditing: existingPaymentMethod?.needsUpdate,
                shouldRetokenize: initialShouldRetokenize,
              }}
            />
            <AddPaymentMethodBankAccountEntry
              show
              setInputErrors={setInputErrors}
              shouldClearDisplayMessages={shouldClearDisplayMessages}
              setShouldClearDisplayMessages={setShouldClearDisplayMessages}
              existingUserName={existingUserName}
              existingZip={existingZip}
              initialPaymentType={initialPaymentType}
            />
          </CreditCardWrapper>
          <AdditionalSettings
            inputErrors={inputErrors}
            setInputErrors={setInputErrors}
            setShouldClearDisplayMessages={setShouldClearDisplayMessages}
            hasMobilePhone={hasMobilePhone}
            sharableWith={sharableWith}
            networkName={networkName}
          />
        </AddPaymentMethodWrapper>
      </AddPaymentMethodModalContent>
    </FnrModal>
  );
}

AddPaymentMethodModal.propTypes = {
  show: PropTypes.bool,
  setShow: PropTypes.func,
  setPaymentMethods: PropTypes.func,
  mustSaveCard: PropTypes.bool,
  userID: PropTypes.number,
  hasMobilePhone: PropTypes.bool,
  sharableWith: PropTypes.array,
  existingPaymentMethod: PropTypes.object,
  hasOtherPaymentMethods: PropTypes.bool,
  isAlreadyExisting: PropTypes.bool,
  existingUserName: PropTypes.string,
  existingZip: PropTypes.string,
  tabID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  networkName: PropTypes.string,
  initialPaymentType: PropTypes.string,
  onSuccess: PropTypes.func,
  initialReplaceExistingCard: PropTypes.bool,
  showUpdateUI: PropTypes.bool,
  initialACHAccountType: PropTypes.oneOf(["checking", "savings", undefined]),
  initialAccountHolderType: PropTypes.oneOf([
    "personal",
    "business",
    undefined,
  ]),
  initialShouldRetokenize: PropTypes.bool,
};

const AddPaymentMethodModalContent = styled.div`
  width: min(400px, calc(100vw - 16px));
  display: flex;
  flex-direction: column;
`;
const CreditCardWrapper = styled.div`
  padding-bottom: 12px;
  position: relative;
  z-index: 100;
  flex-shrink: 0;
`;
